import React, { useState, useEffect, useMemo } from 'react';

import AppBar from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import GroupedList from 'components/GroupedList';
import GroupedItem from 'components/GroupedList/components/GroupedItem';
import ModalHeader from 'components/ModalHeader';
import { Desktop, Mobile } from 'components/Responsive';
import Search from 'components/Search';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { GroupedListModel } from 'models/groupedList';
import { SpecificList as SpecificProps } from 'models/specificList';
import { SpecificRoute } from 'models/specificRoute';
import { htmlT } from 'utils/generic';

import { List, BoxContainer, ButtonGhost, Scaffold, ButtonContainer, StyledButton } from '../styles';

interface Props {
  items: SpecificRoute[];
  countryCurrency: string;
  countryCulture: string;
  show: boolean;
  isRoutes?: boolean;
  onRemove: (item: SpecificRoute[]) => void;
  onCloseModal: () => void;
  onOpenSpecificRoute: () => void;
}

const SpecificRoutesList = ({
  items,
  show,
  isRoutes,
  countryCurrency,
  onRemove,
  onCloseModal,
  onOpenSpecificRoute,
}: Props) => {
  const { t } = useStoreTranslation();

  const [showSearch, setShowSearch] = useState(false);
  const [listResult, setListResult] = useState<GroupedListModel[]>([]);
  const [itemToRemove, setItemToRemove] = useState<SpecificProps[]>([]);
  const [itemList, setItemList] = useState<GroupedListModel[]>([]);

  const searchResultsText = htmlT(t, 'rule.specificLists.searchResults', { count: listResult?.length });

  const selectList: GroupedListModel[] = useMemo(
    () =>
      items.map((item: SpecificProps, index) => {
        if (isRoutes) {
          return {
            id: index.toString(),
            name: `${item.departurePoint}-${item.arrivalPoint}`,
            currency: countryCurrency,
            otherProps: {
              arrivalPoint: item.arrivalPoint,
              departurePoint: item.departurePoint,
              maxPrice: item.maxPrice,
              specificFlightAnticipationDays: item.specificFlightAnticipationDays,
              specificFlightFlexibility: item.specificFlightFlexibility,
            },
          };
        }
        if (item.pickup) {
          return {
            id: index.toString(),
            name: `${item.pickup?.name} - ${item.dropoff?.name}`,
            currency: countryCurrency,
            otherProps: {
              pickup: item.pickup,
              dropoff: item.dropoff,
              maxPrice: item.maxPrice,
              dateVehicle: item.dateVehicle,
            },
          };
        }
        return {
          id: index.toString(),
          name: item.destinationPoint?.name,
          currency: countryCurrency,
          otherProps: {
            destinationPoint: item.destinationPoint,
            maxPrice: item.maxPrice,
            dateHotel: item.dateHotel,
          },
        };
      }),
    [items, isRoutes, countryCurrency],
  );

  useEffect(() => {
    setItemList(
      selectList.sort((a, b) => {
        if (a.name && b.name) {
          return a.name > b.name ? 1 : -1;
        }
        return -1;
      }),
    );
    setItemToRemove([]);
  }, [selectList]);

  const handleTrashClick = (item: GroupedListModel) => {
    const itemRemovedList = itemList.filter((i) => i.id !== item.id);

    setItemList(itemRemovedList);
    setItemToRemove([...itemToRemove, item.otherProps]);
  };

  const handleConfirm = () => {
    onRemove(itemToRemove);
    onCloseModal();
  };

  const handleOnSearch = (value: string) => {
    if (value) {
      setListResult(
        itemList.filter((name: GroupedListModel) => name.name && name.name.toLowerCase().includes(value.toLowerCase())),
      );
    } else setListResult([]);
  };

  return (
    <ApplicationOverlayContent sidebar show={show} onClose={onCloseModal}>
      <Scaffold>
        <Mobile>
          <AppBar goBack={onCloseModal}>
            <h5>{t('rule.specificLists.routesTitle')}</h5>
          </AppBar>
        </Mobile>

        <Desktop>
          <ModalHeader title={t('rule.specificLists.routesTitle')} />
        </Desktop>

        <BoxContainer>
          <ButtonGhost onClick={onOpenSpecificRoute}>
            <ButtonGhost.Icon src="/corporate/images/specific-routes/route-act.svg" />
            {t('rule.specificRoute.btnSpecific')}
          </ButtonGhost>
        </BoxContainer>
        <Search
          show={itemList.length >= 10}
          onShowSearch={(isSearch: boolean) => setShowSearch(isSearch)}
          onSearch={handleOnSearch}
          fullWidth
        />
        {showSearch ? (
          <List>
            <GroupedItem
              quantityText={searchResultsText}
              isSearch={showSearch}
              items={listResult}
              selectableType="trashcan"
              onSelection={(item: GroupedListModel) => handleTrashClick(item)}
              subItems
            />
          </List>
        ) : (
          <>
            <GroupedList
              items={itemList}
              isSearch={showSearch}
              selectableType="trashcan"
              onSelection={(item: GroupedListModel) => handleTrashClick(item)}
              subItems
              isAlphabetic
            />
          </>
        )}
        {itemToRemove.length > 0 && (
          <ButtonContainer onClick={handleConfirm}>
            <StyledButton value={t('rule.specificLists.btnConfirm')} />
          </ButtonContainer>
        )}
      </Scaffold>
    </ApplicationOverlayContent>
  );
};

export default SpecificRoutesList;
